import type { ClientConfig } from '../types';

/**
 * DF.
 *
 * The palette below is DF's own dark theme; the feature set still starts from
 * 99h's, so switch off anything DF should not ship.
 */
export const clientDf: ClientConfig = {
  id: 'df',
  // Shown in the PWA manifest and the app header.
  name: 'DF',
  nameByLocale: {
    zh: '东风国际',
    // Traditional rendering of the same name, for the zh-tw build.
    'zh-tw': '東風國際',
  },

  // Every piece of artwork in this client has Chinese set into it, so the
  // chrome opens in Chinese too rather than following the handset. The
  // language picker still overrides it.
  defaultLocale: 'zh',

  // DF ships its own badge set starting at vip00.png, so an unranked account
  // gets its own plate instead of borrowing VIP1's.
  minVipLevel: 0,
  // 300x88 plates with the crown and the wording in one image.
  vipBadgeHasLabel: true,
  // One header title across the client, matching Discover and Home.
  pageTitleSize: 'lg',
  // The shared copy names 99h in 156 places; this is what it reads as here.
  copyBrandName: '东风国际',
  // The bare chevron from /mine/settings, on every screen.
  backButton: 'plain',
  theme: {
    // Values below are read off the design mock rather than a token export —
    // close, not authoritative. Every one of them is a single line here, so
    // correcting a hue against the real spec costs one edit and no component
    // changes.
    app: '#F2F3F7',
    surface: '#FFFFFF',
    text: '#1C1B2E',
    muted: '#9A9AAD',
    // The announcement bar's running copy. Darker than `muted`: the bar is
    // a white slab on a near-white page, and the grey that reads correctly
    // for a resting nav label is too faint for a line of text on it.
    'text-secondary': '#5A5A6E',
    // One purple across the client: the CTA, the selected tile, the active
    // nav label and the links are all the same hue, the way DF's green was.
    accent: 'var(--color-brand)',
    'accent-text': '#FFFFFF',
    link: 'var(--color-brand)',
    danger: '#FF3B30',
    brand: '#007aff',
    // The lavender wash behind the "announcements" header button and the
    // resting category tiles.
    'brand-soft': '#F3EEFF',
    line: '#EFEFF4',
    // Cards on this design are white slabs, not gradients.
    card: '#FFFFFF',
    'card-border': '#FFFFFF',
    panel: '#FFFFFF',
    // The header balance lives in the account panel now, not a header pill,
    // but the tokens still back the panel's figure and its refresh control.
    pill: '#FFFFFF',
    'pill-action': 'transparent',
    'pill-action-border': 'transparent',
    'pill-action-text': 'var(--color-muted)',
    'header-btn': 'var(--color-brand-soft)',
    'header-btn-border': 'transparent',
    sheet: '#FFFFFF',
    'sheet-contrast': 'var(--color-brand)',
    'sheet-contrast-text': '#FFFFFF',
    // The page tone: on a white card a recessed value reads as the page
    // showing through, which is the light-client rule.
    field: 'var(--color-app)',
    'sheet-border': '#FFFFFF',
    // Both are a faint wash of `brand` over white, at the same lightness the
    // purple-era values had — so the table keeps its header > stripe > card
    // ordering and only the cast changes. 5.9% and 3.1% respectively.
    'table-head': '#F0F7FF',
    'table-stripe': '#F7FBFF',
    track: '#EFEFF4',
    'page-glow': 'none',
    positive: '#17B26A',
    'status-pending': '#FFF4E5',
    'status-pending-text': '#B54708',
    'status-failed': '#FFE9E8',
    'status-failed-text': '#D92D20',
    'status-success': '#E7F8F0',
    'status-success-text': '#17B26A',
    // A 6% tint of `brand` over white — the same strength the lavender
    // `brand-soft` was against the old purple. It backs a SELECTED row with
    // dark text and a `brand` radio on top, so it has to stay pale.
    'option-active': '#EFF7FF',
    'warn-soft': '#FFF4E5',
    gain: 'var(--color-positive)',
    loss: '#EE2C2C',
    tabbar: '#FFFFFF',
    'tabbar-band': 'var(--color-app)',
    'tabbar-active': 'var(--color-brand)',
    'tabbar-active-text': '#FFFFFF',
    'nav-inactive': 'var(--color-muted)',
    // The bar's artwork draws its own top edge, so a hairline here would
    // cut a straight line across the rounded corners.
    'nav-border': 'transparent',
    // No protruding deposit button in this design — the bar is flat.
    'nav-bump': 'transparent',
    'tab-active': 'var(--color-brand-soft)',
    'tab-inactive': 'var(--color-muted)',
    control: '#FFFFFF',
    'control-border': 'var(--color-line)',
    disabled: '#E4E4ED',
    chip: '#FFFFFF',
    'chip-text': 'var(--color-muted)',
    'chip-border': 'var(--color-line)',
    skeleton: '#F0F0F5',
    'skeleton-shine': '#E4E4ED',

    // The CTA gradient — the download button, the selected category tile and
    // every "enter game" pill. A full `background` value, so it is applied
    // inline the way `card` is.
    'brand-gradient': '#007aff',
    'brand-gradient-text': '#FFFFFF',
    // The "recommended" corner flag on a showcase card.
    badge: 'linear-gradient(180deg, #FF7A52 0%, #F5442E 100%)',
    'badge-text': '#FFFFFF',
    'nav-badge': '#F5442E',
    'nav-badge-text': '#FFFFFF',
    // The oversized category word behind a showcase card's artwork.
    watermark: 'rgba(28, 27, 46, 0.06)',
  },

  assets: {
    // TODO(df): point at DF's own bucket once its artwork is uploaded.
    cdn: 'https://pub-dbd64b2aa45940c2ba68ae732da32aa0.r2.dev',

    logo: '/home/icons/logo.png',

    // DF's own category-card artwork, same six filenames as the shared set.
    categoryArt: 'https://pub-dbd64b2aa45940c2ba68ae732da32aa0.r2.dev/DF',
    cardPointer: '/home/icons/titlePointer.png',

    // DF's own copy glyph, drawn everywhere the shared one was. Not under
    // `Mine/` or `wallet/` like the two files it replaces — it answers for
    // both, so it belongs to neither.
    copyIcon: '/icons/copy.png',

    // The browser tab. Reuses the 180px home-screen icon rather than adding
    // a second file — browsers downscale it, and one file cannot drift from
    // the other.
    // The browser tab. Its own file rather than the 180x180 touch icon:
    // that one is sized for the iOS home screen and still carries the old
    // green mark.
    favicon: '/pwa/icons/favicon.png',

    // DF's bottom nav. The four side tabs are single-colour silhouettes, so
    // they are masked and tinted like the built-in SVGs — one file covers
    // both the active and inactive states. The deposit button is full-colour
    // artwork and is drawn as-is.
    icons: {
      // The announcement bar's glyph. Full-colour artwork with its own
      // violet gradient, so it is drawn as-is rather than masked — masking
      // would flatten it to a single brand tone. Replaces the shared SVG,
      // which carries a fixed blue and cannot follow this palette.
      speaker: { src: '/home/icons/speaker.png' },

      'nav-home': { src: '/nav/home.png', mask: true },
      'nav-game': { src: '/nav/game.png', mask: true },
      'nav-activity': { src: '/nav/activity.png', mask: true },
      'nav-mine': { src: '/nav/mine.png', mask: true },
      // Full-colour artwork, drawn at DF's own 30x30 rather than the 54 the
      // built-in FAB uses.
      // 8px below the bump's centre line — 1.5rem from the nav's top edge.
      'nav-deposit': { src: '/nav/deposit.png', size: 30, offsetY: 8 },

      // Game-page tab glyphs. Single-colour silhouettes like the nav icons,
      // so they mask and take the active tab's brand colour.
      'game-tab-live-casino': { src: '/game/tabs/liveCasino.png', mask: true },
      'game-tab-slots': { src: '/game/tabs/slots.png', mask: true },
      'game-tab-hash': { src: '/game/tabs/hashGame.png', mask: true },
      'game-tab-sports': { src: '/game/tabs/sports.png', mask: true },
      'game-tab-card': { src: '/game/tabs/card.png', mask: true },
      'game-tab-fishing': { src: '/game/tabs/fishing.png', mask: true },
      'game-tab-lottery': { src: '/game/tabs/lottery.png', mask: true },
    },
  },

  // The artwork-led lobby: a category strip over a vertical list of
  // provider showcase cards. Every path below is served by the asset
  // overlay from `public/clients/df/`, so dropping a file in makes it
  // appear with no code change.
  home: {
    design: 'v3',

    // One fixed banner rather than the backend's slide carousel — DF's
    // artwork is not managed in the CMS, so the endpoint is never called,
    // and the carousel's dots hide themselves at a single slide.
    //
    // Served from the asset overlay rather than the CDN so it is versioned
    // with the client it belongs to. 1500x632 against the carousel's
    // 332x134 box: a ~2% crop top and bottom, which the artwork's centred
    // composition absorbs.
    banner: '/home/banner.png',

    quickActions: [
      { id: 'deposit', to: '/deposit', icon: '/home/actions/deposit.png', labelKey: 'qa_deposit' },
      { id: 'withdraw', to: '/withdraw', icon: '/home/actions/withdraw.png', labelKey: 'qa_withdraw' },
      { id: 'vip', to: '/mine/vip', icon: '/home/actions/vip.png', labelKey: 'qa_vip' },
    ],

    // The strip's order, which is the design's rather than the Game page's.
    // Each tile is a finished image carrying its own background and label,
    // so nothing here repeats the label — it is only needed as the button's
    // accessible name, and the Game page's own label answers for that.
    // 290x168 artwork, so the strip is told its ratio rather than assuming
    // the near-square default.
    categoryTileRatio: '290 / 168',
    categories: [
      { id: 'slots', icon: '/home/categories/slots.png', activeIcon: '/home/categories/slots-active.png' },
      { id: 'lottery', icon: '/home/categories/lottery.png', activeIcon: '/home/categories/lottery-active.png' },
      { id: 'sports', icon: '/home/categories/sports.png', activeIcon: '/home/categories/sports-active.png' },
      { id: 'live', icon: '/home/categories/live.png', activeIcon: '/home/categories/live-active.png' },
    ],

    // The showcase list, keyed by category. Each entry is a finished card —
    // mark, title, subtitle and call-to-action are painted into the artwork,
    // so nothing here repeats them. Order is display order.
    //
    // `provider` is the catalog's own spelling (see PROVIDER_TITLE in
    // src/api/game.ts); each was read off the card's own branding and
    // checked against that table. A card without one would fall back to the
    // whole category.
    //
    // 棋牌 and 捕鱼 carry no cards: the artwork for them has not been
    // supplied, and both are already declared in `game.emptyTabs` below.
    // Their strip tiles stay, and HomeV3 skips a category with no cards.
    showcase: {
      slots: [
        { id: 'slot1', image: '/home/showcase/slot1.png', provider: 'PGSOFT', recommended: true },
        { id: 'slot2', image: '/home/showcase/slot2.png', provider: 'PragmaticPlay' },
      ],
      lottery: [
        { id: 'lottery1', image: '/home/showcase/lottery1.png', provider: 'CANADA28', recommended: true },
      ],
      sports: [
        { id: 'sports1', image: '/home/showcase/sports1.png', provider: 'SABASports', recommended: true },
        { id: 'sports2', image: '/home/showcase/sports2.png', provider: 'CRSports' },
      ],
      live: [
        { id: 'casino1', image: '/home/showcase/casino1.png', provider: 'DBLIVE', recommended: true },
        { id: 'casino2', image: '/home/showcase/casino2.png', provider: 'SolaireLive' },
      ],
    },

    // The floating rail. Empty until the artwork lands — the rail hides
    // itself rather than reserving an empty column.
    promos: [],
  },

  // DF carries no fishing or card games yet; both tabs stay in the strip and
  // show the empty state.
  // DF funds accounts through a finance contact rather than on-chain.
  // TODO(df): `account` is a placeholder — swap for the backend's finance
  // account once that endpoint exists.
  wallet: {
    mode: 'agent',
    design: 'v3',
    // From the design mock. TODO(df): the backend has no concept of CNY —
    // it neither converts nor enforces this floor, so both are display-and-
    // client-side only until an endpoint serves them.
    minWithdrawal: 100,
    amountCurrency: 'CNY',
    // See the TODO on `cnyRate` — this has to come from the backend before
    // launch.
    cnyRate: 6.79,
    // Home's quick actions and Mine's grid both carry 存款 and 提现, so the
    // bottom switch is a third route between two pages that already link
    // to each other.
    switcher: false,
    channels: [
      { id: 'wechat', label: 'Wechat', icon: '/wallet/icons/wechat.png', account: '@DFGJCW' },
      { id: 'alipay', label: 'AliPay', icon: '/wallet/icons/alipay.png', account: '@DFGJCW' },
    ],
  },

  // The agent wallet has no on-chain address and no payment password, and
  // account binding happens through the finance contact.
  // Affiliate recruitment. Three finished posters; the page only stacks
  // them and floats the pill. The CTA opens live chat, which is what the
  // poster itself tells the reader to do.
  jointVenture: {
    hero: '/joint-venture/hero.png',
    info: '/joint-venture/info.png',
    cta: '/joint-venture/cta.png',
  },

  // The Settings page. `v3` is the short list in the newer design: three
  // device preferences, two information links, sign-out. `hiddenRows` is
  // kept for the record but has nothing to hide on that page — it is a
  // different list, not a filtered one.
  settings: {
    design: 'v3',
    hiddenRows: ['binding_account', 'payment_password', 'wallet_address'],
  },

  // The artwork-led account page. Every image is served by the asset
  // overlay from `public/clients/df/`.
  mine: {
    design: 'v3',
    cardRefresh: false,
    signOutIcon: false,

    // The balance card and the panel under it are ONE image — the notch
    // where they meet cannot be drawn as two backgrounds.
    balancePanelBg: '/Mine/bg/balance-panel.png',

    features: [
      { id: 'vip', image: '/Mine/icons/feature-vip.png', to: '/mine/vip', label: 'VIP特权' },
      // `features.referralProgram` is off, which hides the referral DASHBOARD.
      // The invitation-link page is registered unconditionally in the router,
      // so this reaches it regardless.
      { id: 'invite', image: '/Mine/icons/feature-invite.png', to: '/mine/referral/invitation', label: '邀请好友' },
    ],

    quickActions: [
      // `deposit` / `withdraw` already exist in the mine namespace, so these
      // need no new locale strings.
      { id: 'deposit', icon: '/Mine/icons/qa-deposit.png', to: '/deposit', labelKey: 'deposit' },
      { id: 'withdraw', icon: '/Mine/icons/qa-withdraw.png', to: '/withdraw', labelKey: 'withdraw' },
      { id: 'account', icon: '/Mine/icons/qa-account.png', to: '/wallet/record', labelKey: 'qa_account' },
      { id: 'bets', icon: '/Mine/icons/qa-bets.png', to: '/mine/game-data', labelKey: 'qa_bets' },
    ],

    menu: [
      { id: 'affiliate', icon: '/Mine/icons/menu-affiliate.png', to: '/mine/joint-venture', labelKey: 'menu_affiliate' },
      { id: 'help', icon: '/Mine/icons/menu-help.png', to: '/support', labelKey: 'menu_help' },
      { id: 'settings', icon: '/Mine/icons/menu-settings.png', to: '/mine/settings', labelKey: 'menu_settings' },
    ],
  },

  // The Discover rows. Editorial placements rather than records, so they
  // are config: which ones run is an operator decision, not something an
  // endpoint can answer.
  //
  // 大舞台, 爆奖公告 and 自媒体达人 were dropped at the operator's request
  // along with their artwork; their i18n keys are kept so a row can come
  // back without touching the locale files. Every row that remains leads
  // somewhere.
  discover: {
    officialNews: {
      hero: '/discover/official-news/hero.png',
    },

    // The offers hall. Same campaigns as the /activity page and the same
    // files — this is the version with the buttons.
    //
    // TODO(df): no promo has a detail page and nothing backs "join" /
    // "claim" / "apply", so those two buttons on each card are drawn inert.
    // Give a card `detailTo` / `ctaTo` as each destination ships.
    offers: [
      {
        id: 'media-recruit',
        image: '/activity/promos/media-recruit.jpg',
        ctaLabelKey: 'offer_cta_join',
      },
      {
        id: 'lottery28',
        image: '/activity/promos/lottery28.png',
        ctaLabelKey: 'offer_cta_claim',
      },
      {
        id: 'first-deposit',
        image: '/activity/promos/first-deposit.jpg',
        ctaLabelKey: 'offer_cta_apply',
      },
      {
        id: 'usdt-withdrawal',
        image: '/activity/promos/usdt-withdrawal.jpg',
        ctaLabelKey: 'offer_cta_claim',
      },
    ],

    entries: [
      {
        id: 'official',
        icon: '/discover/icons/official.png',
        labelKey: 'official',
        to: '/discover/official-news',
        // The client's own mark, as the mock has it.
        art: '/home/icons/logo.png',
        badge: true,
      },
      {
        id: 'announcements',
        icon: '/discover/icons/announcements.png',
        labelKey: 'announcements',
        to: '/discover/announcements',
      },
      { id: 'offers', icon: '/discover/icons/offers.png', labelKey: 'offers', to: '/discover/offers' },
    ],
  },

  // The artwork-led promotions page: filter chips over a column of
  // finished banners. Curated here rather than fetched — each is artwork
  // for a specific campaign — so the activities endpoint is never called.
  activity: {
    design: 'v3',

    categories: [
      { id: 'all', labelKey: 'cat_all_promos' },
      { id: 'sports', labelKey: 'cat_sports_esports' },
      { id: 'lottery28', labelKey: 'cat_lottery28' },
      { id: 'vip', labelKey: 'cat_vip_growth' },
    ],
    tabArt: {
      active: '/activity/chrome/tab-active.png',
      inactive: '/activity/chrome/tab-inactive.png',
    },

    // TODO(df): none of these have a detail page yet, so each is drawn
    // inert. Give one a `to` as its page ships.
    promos: [
      {
        id: 'media-recruit',
        image: '/activity/promos/media-recruit.jpg',
        categories: ['vip'],
      },
      {
        id: 'lottery28',
        image: '/activity/promos/lottery28.png',
        categories: ['lottery28'],
        tag: { image: '/activity/chrome/tag-daily.png', labelKey: 'tag_daily' },
      },
      {
        id: 'first-deposit',
        image: '/activity/promos/first-deposit.jpg',
        categories: ['vip'],
        tag: { image: '/activity/chrome/tag-newcomer.png', labelKey: 'tag_newcomer' },
      },
      {
        id: 'wager-rewards',
        image: '/activity/promos/wager-rewards.png',
        categories: ['sports'],
        tag: { image: '/activity/chrome/tag-limited.png', labelKey: 'tag_limited' },
      },
    ],
  },

  game: {
    design: 'v3',
    emptyTabs: ['fishing', 'card'],
  },

  // Official customer service. Drives the Telegram row on the Support page
  // AND the first-withdrawal notice, which names this handle — without it
  // that notice is not rendered at all.
  support: { telegram: '@u8kf' },

  // The design's bar: five destinations, no protruding deposit button, and
  // full-colour artwork rather than tinted silhouettes — so each tab needs
  // its own selected-state file.
  nav: {
    // The bar's own artwork: a flat panel with rounded top corners, which a
    // background colour cannot express. It replaces the `surface` fill and
    // the drop shadow above it.
    background: '/nav/background.png',

    tabs: [
      { id: 'lobby', route: '/', icon: '/nav/lobby.png', activeIcon: '/nav/lobby-active.png', labelKey: 'lobby' },
      { id: 'promotions', route: '/activity', icon: '/nav/promotions.png', activeIcon: '/nav/promotions-active.png', labelKey: 'promotions' },
      { id: 'support', route: '/support', icon: '/nav/support.png', activeIcon: '/nav/support-active.png', labelKey: 'support' },
      { id: 'mine', route: '/mine', icon: '/nav/mine-tab.png', activeIcon: '/nav/mine-tab-active.png', labelKey: 'mine' },
    ],
  },

  // The auth screens. `v3` is the artwork-led design: a full-bleed hero with
  // the form on a floating card over it.
  login: {
    design: 'v3',
    background: '/login/bg.png',
    // The mock's register card is three fields — account, password, confirm —
    // and nothing else.
    //
    // TODO(df): `telegram_info` then goes up as an empty string. The register
    // endpoint accepts that today; confirm with the backend that it stays
    // optional, because there is no field here for the user to fill if it
    // ever stops being.
    telegramContactField: false,
    // Hidden, not dropped: a code arriving by /r/:code or by the Mini App's
    // startapp parameter is still submitted.
    invitationField: false,
  },

  // Starts with everything on, matching 99h. Turning a flag off removes that
  // capability from the DF build only.
  features: {
    originalGames: true,
    // No agent tree in the mock's Mine list.
    referralProgram: false,
    telegramAuth: true,
    // DF's login screens are account + password only.
    tgQuickSignIn: false,
    // The v3 sign-in card carries an "automatic login" tick.
    rememberPassword: true,
    pwaBanner: false,
    liveChat: true,
    // The scrolling announcement bar, between the banner and the account
    // panel. On means `/api/v1/marquees` is requested; an empty response
    // falls back to the `default_notice` string rather than an empty bar.
    noticeMarquee: true,
    // DF runs no operator announcements, so nothing pops up over Home or
    // over a fresh sign-in, and neither notification endpoint is called.
    notifications: false,
    followTelegramTheme: false,
    accountInHeader: true,
  },
};
